import {cn} from 'platform/shared-tw';

import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';

import {getMiraUiLabelTranslationKey} from '../../../../utils/getMiraUiLabelTranslationKey';
import {createKeyDownHandler} from './partVariants';

export type PartMode = 'diagram' | 'standalone';

export interface PartSvgProps {
  readonly mode?: PartMode;
  readonly onClick?: () => void;
  readonly label: string;
  readonly width: number;
  readonly height: number;
  readonly viewBox: string;
  readonly position: {
    readonly top?: number;
    readonly right?: number;
    readonly bottom?: number;
    readonly left?: number;
  };
  readonly children: ReactNode;
}

export function PartSvg(props: PartSvgProps) {
  const {t} = useTranslation();
  const isStandalone = props.mode === 'standalone';
  const labelKey = getMiraUiLabelTranslationKey(props.label);
  const translatedLabel = isNil(labelKey) ? props.label : t(labelKey);

  if (isStandalone) {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox={props.viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        className="block size-full [&_*]:[pointer-events:none]"
        aria-hidden="true"
      >
        {props.children}
      </svg>
    );
  }

  const clickable = isNotNil(props.onClick);

  return (
    <svg
      width={props.width}
      height={props.height}
      viewBox={props.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        'absolute focus:outline-none focus-visible:outline-none',
        clickable ? 'cursor-pointer group' : 'pointer-events-none'
      )}
      style={props.position}
      onClick={props.onClick}
      onKeyDown={clickable ? createKeyDownHandler(props.onClick) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? translatedLabel : undefined}
    >
      {props.children}
    </svg>
  );
}
